'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export default function PromiseSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const benefits = [
    {
      icon: '⚡',
      title: 'Never get caught with a dead lighter again',
      description: 'USB rechargeable battery that lasts weeks per charge'
    },
    {
      icon: '📦',
      title: 'Always have fresh, protected cigarettes ready',
      description: 'Built-in case keeps your smokes unbroken and dry'
    },
    {
      icon: '💎',
      title: 'Carry something that actually looks and feels premium',
      description: 'Military-grade aluminum that commands respect'
    },
    {
      icon: '💰',
      title: 'Stop wasting money on disposable junk',
      description: 'One-time investment that pays for itself in weeks'
    }
  ];

  return (
    <section ref={ref} className="py-12 sm:py-16 md:py-24 lg:py-32 bg-black">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-10 md:mb-16"
        >
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-3 sm:mb-4 md:mb-6 leading-tight">
            Finally: A Lighter That Works Every Single Time
          </h2>
          <p className="text-white/80 text-sm sm:text-base md:text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed">
            This isn&apos;t just another lighter. It&apos;s a tactical-grade tool that combines premium functionality with unbreakable durability.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 max-w-4xl mx-auto">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 sm:p-6 md:p-8 hover:bg-white/10 transition-all duration-300 cursor-default"
            >
              <div className="text-3xl sm:text-4xl md:text-5xl mb-3 sm:mb-4">{benefit.icon}</div>
              <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-2 sm:mb-3 leading-snug">
                {benefit.title}
              </h3>
              <p className="text-white/70 text-xs sm:text-sm md:text-base leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-8 sm:mt-10 md:mt-12"
        >
          <p className="text-white/60 text-xs sm:text-sm md:text-base italic">
            What changes in your life when you upgrade to quality
          </p>
        </motion.div>
      </div>
    </section>
  );
}
